import DayGridAllDayComponent from './DayGridAllDayComponent';
import DayGridAllDayHeaderComponent from './DayGridAllDayHeaderComponent';
import DayGridContextProvider from './DayGridContextProvider';
import DayGridInDayComponent from './DayGridInDayComponent';
import DayGridInDayContentComponent from './DayGridInDayContentComponent';
import { getDayGridItemConfig } from './DayGridItemConfig';
import { dayGridFreeBusyTemplate } from './DayGridTemplates';
import SurfaceGridHandlerAdapter, { GridClickParams } from '../utils/SurfaceGridHandlerAdapter';
import { observer } from 'mobx-react-lite';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import type { CalendarDayContent } from 'owa-calendar-surface-core';
import { CalendarEvent } from 'owa-calendar-types';
import type { ClientItemId } from 'owa-client-ids';
import { getLightColorScheme } from 'owa-color-utils/lib/colorUtils';
import { OwaDate } from 'owa-datetime';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import React from 'react';
import SurfaceEventHandlerAdapter, {
    SurfaceMouseEvent,
    SurfaceKeyboardEvent,
} from '../utils/SurfaceEventHandlerAdapter';
import {
    updateAllDayWellExpanded,
    updateHoveredItem,
    CalendarSurfaceTransformProps,
} from 'owa-calendar-surface-dategrid';
import {
    CalendarEventInteraction,
    CalendarEventInteractionCallback,
    KeyboardCalendarEventInteraction,
    getKeyboardCalendarEventInteraction,
    isKeyboardCalendarEventInteractionOpen,
    isKeyboardCalendarEventInteractionDelete,
    isKeyboardCalendarEventInteractionForwardEvent,
    isKeyboardCalendarEventInteractionReply,
    isKeyboardCalendarEventInteractionReplyAll,
} from 'owa-calendar-event-interaction-schema';
import {
    canDragItem,
    canDropItemType,
    getSurfaceItemForPreview,
    onDragEnd,
    onDragStart,
    onItemDropped,
} from 'owa-calendar-surface-dategrid-common-dnd-callbacks';
import { DraggableDayGrid, createDayGridConfig } from 'owa-draggable-day-grid';
import { getScenarioStore, shouldGrayOutItem, DayGridViewState } from 'owa-day-grid-core-schema';
import type { PerformanceDatapoint } from 'owa-analytics';

export interface DayGridViewProps {
    scenario: string;
    /** Enables gray shading of non-working hours and past days
     *  and auto-scrolling to show those hours -- defaults to true */
    enableWorkingHours?: boolean;
    selectedEventId?: ClientItemId;
    maxAllDayRowsToShow?: number;
    highlightedEventId?: string;
    calendarEventInteractionCallback?: CalendarEventInteractionCallback;
    shouldSortAllDaySelectionToFront?: boolean;
    onGridClick?: (params: GridClickParams & { viewState: DayGridViewState }) => void;
    onGridDoubleClick?: (params: GridClickParams & { viewState: DayGridViewState }) => void;
    onGridMouseDown?: (ev: React.MouseEvent<HTMLElement>) => void;
    handleEditOnItemDrop?: (event: Partial<CalendarEvent>, eventHasUpdates: boolean) => void;
    numDaysInDayView?: number | undefined;
    greyOutUnselectedItems?: boolean;
    shouldHighlightColumnAllDayBusyAway?: boolean;
    perfDatapoint?: React.RefObject<PerformanceDatapoint | undefined>;
}

const DayGridView = observer(function DayGridView(props: DayGridViewProps) {
    const {
        scenario,
        enableWorkingHours = true,
        maxAllDayRowsToShow,
        highlightedEventId,
        shouldSortAllDaySelectionToFront,
        calendarEventInteractionCallback,
        onGridClick,
        onGridDoubleClick,
        onGridMouseDown,
        handleEditOnItemDrop,
        numDaysInDayView,
        greyOutUnselectedItems = true,
        shouldHighlightColumnAllDayBusyAway = true,
        perfDatapoint,
    } = props;
    const store = getScenarioStore(scenario);

    const onClick = (
        id: ClientItemId,
        parentFolderId: string,
        event: SurfaceMouseEvent | SurfaceKeyboardEvent
    ) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.Click,
            id: id,
            target: event.currentTarget,
        });
    };
    const onClickHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onClick),
        []
    );

    const onDoubleClick = (id: ClientItemId, parentFolderId: string, event: SurfaceMouseEvent) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.DoubleClick,
            id: id,
            target: event.currentTarget,
        });
    };
    const onDoubleClickHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onDoubleClick),
        []
    );

    const onContextMenu = (id: ClientItemId, parentFolderId: string, event: SurfaceMouseEvent) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.RightClick,
            id: id,
            parentFolderId: parentFolderId,
            evt: event,
        });
    };
    const onContextMenuHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>(onContextMenu, true),
        []
    );
    const onMouseDownHandler = React.useCallback(
        (evt: React.MouseEvent<HTMLDivElement>) => {
            // This is needed to prevent the column from getting the mouse down events.
            if (!store?.enableDragDrop) {
                evt.stopPropagation();
            }
        },
        [store?.enableDragDrop]
    );
    const onMouseOverHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceMouseEvent>((_, __, ___, item: CalendarEvent) =>
            updateHoveredItem(item, store)
        ),
        [store]
    );
    const onMouseOutHandler = React.useCallback(() => updateHoveredItem(null, store), [store]);

    const onSurfaceGridClick = (scenario: string, params: GridClickParams) => {
        onGridClick?.({ ...params, viewState: getScenarioStore(scenario) });
    };
    const onSurfaceGridClickHandler = React.useCallback(
        SurfaceGridHandlerAdapter(scenario, onSurfaceGridClick),
        [scenario]
    );

    const onSurfaceGridDoubleClick = (scenario: string, params: GridClickParams) => {
        onGridDoubleClick?.({
            ...params,
            viewState: getScenarioStore(scenario),
        });
    };
    const onSurfaceGridDoubleClickHandler = React.useCallback(
        SurfaceGridHandlerAdapter(scenario, onSurfaceGridDoubleClick),
        [scenario]
    );

    const onKeyDown = (
        id: ClientItemId,
        parentFolderId: string,
        event: SurfaceKeyboardEvent,
        keyboardCalendarEventInteraction: KeyboardCalendarEventInteraction
    ) => {
        calendarEventInteractionCallback?.({
            type: CalendarEventInteraction.KeyDown,
            id: id,
            target: event.currentTarget,
            keyboardCalendarEventInteraction: keyboardCalendarEventInteraction,
        });
    };
    const onKeydownHandler = React.useCallback(
        SurfaceEventHandlerAdapter<SurfaceKeyboardEvent>(
            (id, parentFolderId, event) => {
                const keyboardCalendarEventInteraction = getKeyboardCalendarEventInteraction(event);

                switch (keyboardCalendarEventInteraction) {
                    case KeyboardCalendarEventInteraction.Open:
                        onClick(id, parentFolderId, event);
                        break;
                    default:
                        onKeyDown(id, parentFolderId, event, keyboardCalendarEventInteraction);
                        break;
                }
            },
            false,
            event => {
                return [
                    isKeyboardCalendarEventInteractionOpen,
                    isKeyboardCalendarEventInteractionDelete,
                    isKeyboardCalendarEventInteractionForwardEvent,
                    isKeyboardCalendarEventInteractionReply,
                    isKeyboardCalendarEventInteractionReplyAll,
                ].some(action => action({ event }));
            }
        ),
        []
    );

    const onItemDroppedHandler = React.useCallback(
        (droppedItem: CalendarEvent, originalItem: CalendarEvent, dragInfo: DragData) => {
            onItemDropped(droppedItem, originalItem, dragInfo, handleEditOnItemDrop);
        },
        [handleEditOnItemDrop]
    );

    const shouldGrayOutItemForScenario = React.useCallback(
        (event: CalendarEvent) => shouldGrayOutItem(highlightedEventId, event),
        [highlightedEventId]
    );

    const onOverflowItemClicked = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            ev.stopPropagation();
            if (store) {
                updateAllDayWellExpanded(store, true, 'ExpandButton');
            }
        },
        [store]
    );

    const viewState = store;

    const getCalendarDayContentConfig = React.useCallback(
        (content: CalendarDayContent, calendarConfig: CalendarSurfaceTransformProps) => ({
            inDayComponent: {
                component: DayGridInDayComponent,
                additionalProps: {
                    viewState,
                },
            },
            inDayContentComponent: {
                component: DayGridInDayContentComponent,
                additionalProps: {
                    viewState,
                    calendarConfig: config,
                },
            },
            allDayComponent: {
                component: DayGridAllDayComponent,
                additionalProps: {
                    viewState,
                    calendarConfig,
                    calendarDayContent: content,
                },
            },
            allDayHeaderComponent: {
                component: DayGridAllDayHeaderComponent,
                additionalProps: {
                    viewState,
                    calendarConfig: config,
                },
            },
        }),
        [viewState]
    );

    if (!viewState) {
        return null;
    }

    const generateColorScheme = (model: CalendarEvent) => {
        return shouldGrayOutItemForScenario(model) && greyOutUnselectedItems
            ? getLightColorScheme()
            : getCalendarEventColorScheme(model);
    };

    const overrideIsSelectedDecorator = (model: CalendarEvent) => {
        return highlightedEventId && !shouldGrayOutItemForScenario(model);
    };

    const baseConfig = createDayGridConfig(viewState);
    const config = {
        ...baseConfig,
        timeStripConfig: {
            ...baseConfig,
            highlightCurrentTime: !highlightedEventId,
        },
        onSurfaceClick: (
            start: OwaDate,
            end: OwaDate,
            isAllDay: boolean,
            isClickAndDragEvent: boolean,
            selectedCalendarFolderId: string
        ) =>
            onSurfaceGridClickHandler({
                start: start,
                end: end,
                isAllDay: isAllDay,
                isClickAndDragEvent: isClickAndDragEvent,
                selectedCalendarFolderId: selectedCalendarFolderId,
            }),
        onSurfaceDoubleClick: (
            start: OwaDate,
            end: OwaDate,
            isAllDay: boolean,
            isClickAndDragEvent: boolean,
            selectedCalendarFolderId: string
        ) =>
            onSurfaceGridDoubleClickHandler({
                start: start,
                end: end,
                isAllDay: isAllDay,
                isClickAndDragEvent: isClickAndDragEvent,
                selectedCalendarFolderId: selectedCalendarFolderId,
            }),
        onSurfaceMouseDown: onGridMouseDown,
        shouldShowCurrentTime: () => true,
        dragDropConfig: {
            canDrag: canDragItem,
            canDrop: canDropItemType,
            onItemDropped: onItemDroppedHandler,
            onDragStart: onDragStart,
            onDragEnd: onDragEnd,
            getSurfaceItemForPreview: getSurfaceItemForPreview,
            colorProvider: getCalendarEventColorScheme,
        },
        getItemConfig: getDayGridItemConfig(
            viewState,
            {
                onClick: onClickHandler,
                onDoubleClick: onDoubleClickHandler,
                onContextMenu: onContextMenuHandler,
                onKeyDown: onKeydownHandler,
                onMouseUp: onMouseUpHandler,
                onMouseDown: onMouseDownHandler,
                onMouseOver: onMouseOverHandler,
                onMouseOut: onMouseOutHandler,
            },
            {
                onClick: onOverflowItemClicked,
            },
            generateColorScheme,
            viewState.enableUpNext /* joinButtonVisible */,
            undefined /* shouldHideTooltip */,
            overrideIsSelectedDecorator
        ),
        freeBusyTemplate: dayGridFreeBusyTemplate,
        disableWorkingHours: !enableWorkingHours,
    };

    return (
        <DayGridContextProvider enableWorkingHours={enableWorkingHours}>
            <DraggableDayGrid
                scenarioId={scenario}
                config={config}
                getCalendarDayContentConfig={getCalendarDayContentConfig}
                maxAllDayRowsToShow={maxAllDayRowsToShow}
                highlightedEventId={highlightedEventId}
                numDaysInDayView={numDaysInDayView}
                shouldSortAllDaySelectionToFront={shouldSortAllDaySelectionToFront}
                shouldHighlightColumnAllDayBusyAway={shouldHighlightColumnAllDayBusyAway}
                perfDatapoint={perfDatapoint}
            />
        </DayGridContextProvider>
    );
});

function onMouseUpHandler(evt: React.MouseEvent) {
    evt.stopPropagation();
}

export default DayGridView;
